import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, PackagePlus, Search } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { useDebounce } from '../../hooks/useDebounce';
import { buildTeamApiService } from '../../services/buildTeamApi';
import {
  BuildItemStatus,
  BuildProject,
  BuildTag,
  BUILD_ITEM_STATUSES,
} from '../../types/buildTeam';
import { cn } from '../../utils/cn';
import { ApogeePackSearchField } from '../apogee/ApogeePackSearchField';

export interface CreateBuildItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId?: string;
}

function formatStatusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildDimensionsJson(x: string, y: string, z: string): string | undefined {
  const o: Record<string, number> = {};
  if (x.trim() !== '') o.x = Number(x);
  if (y.trim() !== '') o.y = Number(y);
  if (z.trim() !== '') o.z = Number(z);
  return Object.keys(o).length > 0 ? JSON.stringify(o) : undefined;
}

function buildCoordinatesJson(world: string, x: string, y: string, z: string): string | undefined {
  const o: Record<string, string | number> = {};
  if (world.trim() !== '') o.world = world.trim();
  if (x.trim() !== '') o.x = Number(x);
  if (y.trim() !== '') o.y = Number(y);
  if (z.trim() !== '') o.z = Number(z);
  return Object.keys(o).length > 0 ? JSON.stringify(o) : undefined;
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function CreateBuildItemModal({
  isOpen,
  onClose,
  onSuccess,
  projectId: initialProjectId,
}: CreateBuildItemModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedProject, setSelectedProject] = useState<BuildProject | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const debouncedProjectSearch = useDebounce(projectSearch, 300);
  const [projectResults, setProjectResults] = useState<BuildProject[]>([]);
  const [isSearchingProjects, setIsSearchingProjects] = useState(false);
  const [status, setStatus] = useState<BuildItemStatus>('planned');
  const [priority, setPriority] = useState(5);
  const [category, setCategory] = useState('');
  const [dimX, setDimX] = useState('');
  const [dimY, setDimY] = useState('');
  const [dimZ, setDimZ] = useState('');
  const [coordWorld, setCoordWorld] = useState('');
  const [coordX, setCoordX] = useState('');
  const [coordY, setCoordY] = useState('');
  const [coordZ, setCoordZ] = useState('');
  const [tags, setTags] = useState<BuildTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modpackApogeeId, setModpackApogeeId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setDescription('');
    setSelectedProject(null);
    setProjectSearch('');
    setProjectResults([]);
    setStatus('planned');
    setPriority(5);
    setCategory('');
    setDimX('');
    setDimY('');
    setDimZ('');
    setCoordWorld('');
    setCoordX('');
    setCoordY('');
    setCoordZ('');
    setSelectedTagIds([]);
    setModpackApogeeId(null);
    setError(null);

    setIsLoadingTags(true);
    buildTeamApiService
      .listTags()
      .then(setTags)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      })
      .finally(() => setIsLoadingTags(false));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !initialProjectId) return;
    buildTeamApiService
      .getProjectById(initialProjectId)
      .then((p) => {
        setSelectedProject({
          id: p.id,
          title: p.title,
          description: p.description,
          status: p.status,
          priority: p.priority,
          category: p.category,
          created_at: p.created_at,
          created_by: p.created_by,
          updated_at: p.updated_at,
          updated_by: p.updated_by,
          started_at: p.started_at,
          due_date: p.due_date,
          completed_at: p.completed_at,
          metadata: p.metadata,
          apogee_id: p.apogee_id,
        });
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      });
  }, [isOpen, initialProjectId]);

  useEffect(() => {
    if (!debouncedProjectSearch || debouncedProjectSearch.length < 1) {
      setProjectResults([]);
      return;
    }
    setIsSearchingProjects(true);
    buildTeamApiService
      .searchProjects(debouncedProjectSearch)
      .then(setProjectResults)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to search projects');
      })
      .finally(() => setIsSearchingProjects(false));
  }, [debouncedProjectSearch]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    const dimensions = buildDimensionsJson(dimX, dimY, dimZ);
    const coordinates = buildCoordinatesJson(coordWorld, coordX, coordY, coordZ);

    setIsSubmitting(true);
    try {
      const payload: Parameters<typeof buildTeamApiService.createBuildItem>[0] = {
        title: title.trim(),
        description: description.trim(),
        project_id: selectedProject?.id ?? null,
        status,
        priority,
        category: category.trim() || undefined,
        dimensions,
        coordinates,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      };
      if (modpackApogeeId != null) payload.apogee_id = modpackApogeeId;
      await buildTeamApiService.createBuildItem(payload);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create build item');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-build-title"
      >
        <div className="modal-header">
          <h2 id="create-build-title" className="modal-title flex items-center gap-2">
            <PackagePlus className="w-5 h-5 text-accent-300" />
            New build item
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              required
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full"
              rows={3}
              required
            />
          </div>

          <ApogeePackSearchField
            value={modpackApogeeId}
            onChange={setModpackApogeeId}
            compact
            description="Optional. Links this build to a pack in Apogee for quick reference."
          />

          <div>
            <label className={LABEL_CLASS}>Project</label>
            {selectedProject ? (
              <div className="flex items-center justify-between p-2 pl-3 rounded-lg bg-accent-500/10 border border-accent-500/20 gap-2">
                <span className="text-sm font-medium text-white truncate">{selectedProject.title}</span>
                <button
                  type="button"
                  onClick={() => setSelectedProject(null)}
                  className="btn-icon btn-icon-sm"
                  aria-label="Clear project"
                  title="Clear project"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  className="glass-input w-full pl-9"
                  placeholder="Search projects…"
                />
                {(projectResults.length > 0 || isSearchingProjects) && projectSearch.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 glass-card overflow-hidden z-10 max-h-48 overflow-y-auto divide-y divide-glass-200/10">
                    {isSearchingProjects && (
                      <div className="p-3 flex items-center justify-center">
                        <Loader className="w-4 h-4 animate-spin text-accent-400" />
                      </div>
                    )}
                    {projectResults.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setSelectedProject(p);
                          setProjectSearch('');
                          setProjectResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-glass-200/5 text-sm text-white"
                      >
                        {p.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BuildItemStatus)}
                className="glass-select w-full"
              >
                {BUILD_ITEM_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASS}>Priority (1–10)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={10}
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  className="flex-1 h-2 rounded-full appearance-none bg-glass-200/30 accent-accent-500"
                />
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={priority}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v >= 1 && v <= 10) setPriority(v);
                  }}
                  className="glass-input w-16 text-center tabular-nums"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="glass-input w-full"
              placeholder="Optional"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Dimensions (x, y, z)</label>
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  value={dimX}
                  onChange={(e) => setDimX(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="X"
                  aria-label="Dimension X"
                />
                <input
                  type="number"
                  value={dimY}
                  onChange={(e) => setDimY(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Y"
                  aria-label="Dimension Y"
                />
                <input
                  type="number"
                  value={dimZ}
                  onChange={(e) => setDimZ(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Z"
                  aria-label="Dimension Z"
                />
              </div>
            </div>

            <div>
              <label className={LABEL_CLASS}>Coordinates</label>
              <input
                type="text"
                value={coordWorld}
                onChange={(e) => setCoordWorld(e.target.value)}
                className="glass-input w-full mb-2"
                placeholder="World name"
                aria-label="World name"
              />
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  value={coordX}
                  onChange={(e) => setCoordX(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="X"
                  aria-label="Coordinate X"
                />
                <input
                  type="number"
                  value={coordY}
                  onChange={(e) => setCoordY(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Y"
                  aria-label="Coordinate Y"
                />
                <input
                  type="number"
                  value={coordZ}
                  onChange={(e) => setCoordZ(e.target.value)}
                  className="glass-input w-full tabular-nums"
                  placeholder="Z"
                  aria-label="Coordinate Z"
                />
              </div>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Tags</label>
            {isLoadingTags ? (
              <div className="p-3 flex items-center justify-center">
                <Loader className="w-4 h-4 animate-spin text-accent-400" />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggleTag(tag.id)}
                    className={cn(
                      'chip transition-colors',
                      selectedTagIds.includes(tag.id)
                        ? 'text-white'
                        : 'hover:text-white hover:border-glass-200/25'
                    )}
                    aria-pressed={selectedTagIds.includes(tag.id)}
                    style={
                      selectedTagIds.includes(tag.id)
                        ? { borderColor: tag.color, backgroundColor: `${tag.color}20` }
                        : undefined
                    }
                  >
                    {tag.name}
                  </button>
                ))}
                {tags.length === 0 && (
                  <span className="text-xs text-glass-400">No tags available</span>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Create build item
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
